import { useState } from "react";
import { useNavigate } from "react-router";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { PermissionAction } from "@colab/shared";
import { useCreateRole } from "@/features/settings/use-create-role";
import { PermissionsField } from "./permissions-field";

export function CreateRoleSheet() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const mutation = useCreateRole();

  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState<Set<PermissionAction>>(
    () => new Set(),
  );

  function reset() {
    setName("");
    setKey("");
    setDescription("");
    setPermissions(new Set());
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    mutation.mutate(
      {
        name: name.trim(),
        key: key.trim(),
        description: description.trim() || null,
        permissions: Array.from(permissions),
      },
      {
        onSuccess: (res) => {
          toast.success("Role created");
          setOpen(false);
          reset();
          if (res.id) navigate(`/admin/settings/roles/${res.id}`);
        },
      },
    );
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <SheetTrigger asChild>
        <Button size="sm">
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          New role
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="font-display tracking-tight">
            Create role
          </SheetTitle>
          <SheetDescription>
            Custom roles can be assigned to internal team members. Choose a
            lowercase key (e.g. <code>commissions_lead</code>) and select the
            actions this role is allowed to perform.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={submit} className="mt-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs">
                Display name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={60}
                placeholder="Commissions Lead"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="key" className="text-xs">
                Key <span className="text-destructive">*</span>
              </Label>
              <Input
                id="key"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                required
                maxLength={40}
                pattern="[a-z][a-z0-9_]*"
                placeholder="commissions_lead"
                className="font-mono"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description" className="text-xs">
              Description
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={2000}
              placeholder="What this role is for. Visible to other admins."
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Permissions</Label>
            <PermissionsField value={permissions} onChange={setPermissions} />
          </div>

          <div className="sticky bottom-0 -mx-6 flex justify-end gap-2 border-t bg-card px-6 py-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create role
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
